import {
  Dispatch,
  Fragment,
  KeyboardEvent,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  ContentHeader,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { securityInsightsApiRef } from '../api/apiRef';
import { SecurityInsightsClient } from '../api/SecurityInsightsClient';
import {
  SecurityIssue,
  IssuesByGroup,
  IssuesBySystemInstance,
  SingleViewFilters,
  TopAsset,
  UNCLASSIFIED_TAXONOMY,
} from '../api/types';
import { SeverityBarChart } from './Charts/SeverityBarChart';
import { GroupByControl } from './GroupByControl';
import { SummaryTable, exportTableCsv } from './SummaryTable';
import { SummaryPlaceholder } from './SummaryPlaceholder';
import { AssetCell, TeamCell } from './EntityCells';
import { buildFindingColumns, centered, dupColumn } from './findingColumns';
import { buildActionsColumn } from './QuiesceActionsColumn';
import { QuiesceDialog } from './QuiesceDialog';
import { useCallerIdentity } from '../hooks/useCallerIdentity';
import { useTaxonomyLabels } from '../hooks/useTaxonomyLabels';
import { assetCandidateRefs, toGroupRef } from '../utils/entityRefHelpers';
import { formatToolName } from '../utils/textFormatting';
import {
  applyOptimisticHide,
  createHideAdjustment,
  HideAdjustment,
  useOptimisticQuiesceHide,
} from '../hooks/useOptimisticQuiesceHide';

export type GroupByMode =
  | 'team'
  | 'pillar'
  | 'category'
  | 'pillarCategory'
  | 'issueType'
  | 'cve'
  | 'systemInstance'
  | 'asset';

/** Modes rendered via `renderRollupMode` — the small in-memory summary
 * table/chart/drill-down shape. `team` and `asset` each have their own
 * special-cased render branch instead (team's is a fixed pair of summary
 * tables plus the full paginated issues table; asset's is a paginated
 * server-side table with its own expandable per-asset detail panel).
 * `pillarCategory` also has its own branch — see `renderPillarCategoryMode` —
 * since it's a nested combination of the (still independently usable, e.g.
 * by SecurityPostureReportPage) `pillar`/`category` rollups rather than a
 * single flat one. `systemInstance` is the same kind of nested table — see
 * `renderSystemInstanceMode` — but it is *not* assembled from two flat
 * rollups: nothing client-side maps a Deployed Instance to its System, so the
 * backend returns the hierarchy in one response. */
type RollupMode = Exclude<
  GroupByMode,
  'team' | 'asset' | 'pillarCategory' | 'systemInstance'
>;

const MODE_LABELS: Record<GroupByMode, string> = {
  team: 'By Team / Experience',
  pillar: 'By Pillar',
  category: 'By Category',
  pillarCategory: 'By Pillar / Category',
  issueType: 'By Tool',
  cve: 'By CVE / Rule',
  systemInstance: 'By System / Deployed Instance',
  asset: 'By Asset',
};

// This panel now owns the idiom that used to live in SingleViewPage.tsx: the
// shared filter panel's search box is bound to `selectedCve`, generalized
// here into a cross-column `search` param and stripped from the literal CVE
// filter so the two don't AND together server-side and hide matches the
// fuzzy search would find via title/asset/etc. instead of the CVE column.
// Every Single View endpoint resolves that `search` to the same matched-issue
// set server-side (`resolveSearchMatchedRows`, PR #1563), so the rollups and
// the paginated table below them agree on what matched.
function withoutCve<T extends SingleViewFilters>(
  filters: T,
): Omit<T, 'selectedCve'> {
  const { selectedCve: _selectedCve, ...rest } = filters;
  return rest;
}

function withSearch<T extends SingleViewFilters>(
  filters: T,
  search: string | undefined,
): Omit<T, 'selectedCve'> & { search?: string } {
  return { ...withoutCve(filters), search };
}

interface DrillConfigBase {
  labelFor?: (code: string) => string;
  exportFilename: string;
}

/** A row stands for one value of one filter field. */
interface FieldDrillConfig extends DrillConfigBase {
  noun: string;
  /** SingleViewFilters field the drill-down click overrides. */
  drillField: keyof SingleViewFilters;
}

/** A row identified by more than one value (a nested row also carries its
 * parent's), so it states its own full scope and heading. */
interface ScopedDrillConfig extends DrillConfigBase {
  drillScope: (key: string) => Partial<SingleViewFilters>;
  headingFor: (key: string) => string;
}

/** What it takes to drill into one row of a rollup and list its issues. */
type DrillConfig = FieldDrillConfig | ScopedDrillConfig;

interface RollupModeConfig extends FieldDrillConfig {
  nameKey: string;
  getRollup: (
    filters: SingleViewFilters & { search?: string },
  ) => Promise<IssuesByGroup[]>;
}

/** The filters a drilled row adds on top of the page's own. */
function drillFilters(
  cfg: DrillConfig,
  key: string,
): Partial<SingleViewFilters> {
  return 'drillScope' in cfg
    ? cfg.drillScope(key)
    : { [cfg.drillField]: key };
}

// A By System / Deployed Instance row is identified by its System, plus its
// instance when it is a nested row. A System is a catalog entity name and an
// instance is a catalog name or a raw resource tag, so neither plausibly holds
// a control character; only the first separator is ever searched for. The pair
// is needed, not just the instance: the `Unattributed` instance exists once per
// System that has instances, so the name alone would drill into every System's
// unattributed findings at once.
const SYSTEM_INSTANCE_KEY_SEPARATOR = '\u001f';

/**
 * The selection key of a System row (no `instance`) or a Deployed Instance row
 * nested under it. Exported so a caller that owns the selection (see
 * `rollupSelection`) can express "System X, instance Y" as the one string the
 * panel compares rows against.
 */
export function systemInstanceRowKey(
  system: string,
  instance?: string,
): string {
  return instance === undefined
    ? system
    : `${system}${SYSTEM_INSTANCE_KEY_SEPARATOR}${instance}`;
}

function parseSystemInstanceRowKey(key: string): {
  system: string;
  instance?: string;
} {
  const at = key.indexOf(SYSTEM_INSTANCE_KEY_SEPARATOR);
  return at === -1
    ? { system: key }
    : {
        system: key.slice(0, at),
        instance: key.slice(at + SYSTEM_INSTANCE_KEY_SEPARATOR.length),
      };
}

/** The bucket the backend gives findings with no deployed instance. */
const UNATTRIBUTED_INSTANCE = 'Unattributed';

/**
 * A System none of whose findings sit on a deployed instance comes back from
 * the backend with a single `Unattributed` instance row that repeats the
 * System's own totals. Drop it so that System is just its System row; clicking
 * it already opens exactly those findings. A System that *does* have instances
 * keeps its `Unattributed` row (its repo-level findings), so the rows beneath
 * it still add up to the System's total.
 */
function withoutLoneUnattributedInstance(
  rows: IssuesBySystemInstance[],
): IssuesBySystemInstance[] {
  return rows.map(row =>
    row.instances.length === 1 &&
    row.instances[0].deployment_instance === UNATTRIBUTED_INSTANCE
      ? { ...row, instances: [] }
      : row,
  );
}

const SYSTEM_INSTANCE_CONFIG: ScopedDrillConfig = {
  drillScope: key => {
    const { system, instance } = parseSystemInstanceRowKey(key);
    return instance === undefined
      ? { selectedSystem: system }
      : { selectedSystem: system, selectedDeployedInstance: instance };
  },
  headingFor: key => {
    const { system, instance } = parseSystemInstanceRowKey(key);
    return instance === undefined
      ? `system: ${system}`
      : `deployed instance: ${instance} (in ${system})`;
  },
  exportFilename: 'issues_by_system_instance.csv',
};

interface RollupState {
  rows: IssuesByGroup[];
  setRows: (rows: IssuesByGroup[]) => void;
  expanded: string | null;
  setExpanded: (value: string | null) => void;
  total: number;
  setTotal: (value: number) => void;
  truncated: boolean;
  setTruncated: (value: boolean) => void;
  tableKey: number;
  setTableKey: Dispatch<SetStateAction<number>>;
  isExporting: boolean;
  setIsExporting: (value: boolean) => void;
  detailsRef: RefObject<HTMLDivElement>;
  versionRef: MutableRefObject<number>;
  hideAdjustmentRef: MutableRefObject<HideAdjustment>;
}

function useRollupState(): RollupState {
  const [rows, setRows] = useState<IssuesByGroup[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const detailsRef = useRef<HTMLDivElement>(null);
  const versionRef = useRef(0);
  const hideAdjustmentRef = useRef(createHideAdjustment());
  return {
    rows,
    setRows,
    expanded,
    setExpanded,
    total,
    setTotal,
    truncated,
    setTruncated,
    tableKey,
    setTableKey,
    isExporting,
    setIsExporting,
    detailsRef,
    versionRef,
    hideAdjustmentRef,
  };
}

function useFetchRollupDetails(
  api: SecurityInsightsClient,
  filters: SingleViewFilters,
  cfg: DrillConfig,
  state: RollupState,
  addRefs: (refs: ReadonlyArray<string | null | undefined>) => void,
  filterHidden: <T extends { tool: string; src_id: string }>(rows: T[]) => T[],
) {
  return useCallback(
    async (query: {
      page: number;
      pageSize: number;
      orderBy?: { field?: string };
      orderDirection?: string;
    }) => {
      if (!state.expanded) return { data: [], page: 0, totalCount: 0 };
      const version = ++state.versionRef.current;
      const search = filters.selectedCve?.trim() || undefined;
      const searchActive = Boolean(search);
      const result = await api.getSingleView({
        ...(withSearch(filters, search) as SingleViewFilters & {
          search?: string;
        }),
        ...drillFilters(cfg, state.expanded),
        page: query.page,
        pageSize: query.pageSize,
        orderBy: searchActive ? undefined : query.orderBy?.field,
        orderDirection: searchActive
          ? undefined
          : (query.orderDirection as 'asc' | 'desc') || undefined,
      } as any);
      if (version !== state.versionRef.current) {
        return { data: [], page: query.page, totalCount: 0 };
      }
      const { data, totalCount: adjustedTotalCount } = applyOptimisticHide(
        result,
        filters.includeQuiesced,
        filterHidden,
        state.hideAdjustmentRef,
      );
      state.setTotal(adjustedTotalCount);
      state.setTruncated(Boolean(result.truncated));
      const refs: Array<string | null> = [];
      for (const row of result.data) {
        refs.push(toGroupRef(row.team ?? ''));
        refs.push(...assetCandidateRefs(row.asset ?? '', row.issue_type));
      }
      addRefs(refs);
      return { data, page: query.page, totalCount: adjustedTotalCount };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, filters, cfg, state.expanded, addRefs, filterHidden],
  );
}

/**
 * The externally-controlled rollup selection, as an all-or-nothing pair: a
 * caller that takes over row selection with `onRollupRowSelect` must also feed
 * the current selection back in through `rollupSelection`, or the panel has no
 * record of what's selected (see each field's own doc comment below). Modelled
 * as a union rather than two optional props so passing one without the other
 * is a type error at the call site instead of a subtle runtime bug.
 */
type RollupSelectionProps =
  | {
      /**
       * Clicking a row/bar in a *rollup* mode (pillar/category/issueType/
       * systemInstance/cve — not team or asset, which have their own
       * dedicated tables) calls this with the field to drill by and the clicked
       * value, instead of expanding this panel's own paginated issues table
       * below the chart. Toggling the same row/bar again calls this with
       * `value: null`. Intended for a caller that already renders its own
       * "Security Issues" table elsewhere on the page (e.g.
       * SecurityPostureReportPage) and wants row clicks here to re-scope *that*
       * table and scroll to it, rather than rendering a second, duplicate
       * issues table right here.
       *
       * `parentScope` is set only for a row that is nested under another (a
       * Deployed Instance under its System) and is that parent's own scope:
       * the row's value alone doesn't identify it, so the caller must apply
       * both. Omitted for every top-level row.
       */
      onRollupRowSelect: (
        drillField: keyof SingleViewFilters,
        value: string | null,
        parentScope?: Partial<SingleViewFilters>,
      ) => void;
      /**
       * The externally-owned selection for the *active* rollup mode. Without
       * it this panel's local `expanded` state would be the only record of
       * what's selected, and it goes stale the moment the scope changes
       * through another surface (a matrix cell click on
       * SecurityPostureReportPage calls `setDrill` directly and never comes
       * through here). A stale value doesn't just mis-highlight a row: the
       * next click on that row is read as "deselect the current selection"
       * and clears the real scope instead of applying the clicked one. Pass
       * `null` for "nothing selected". For `systemInstance` it is the row key
       * from `systemInstanceRowKey` (a System, or a System + one of its
       * instances).
       */
      rollupSelection: string | null;
    }
  /**
   * The uncontrolled case — rollup modes keep managing their own
   * expand-and-fetch behavior, which is what SingleViewPage wants: it has no
   * other issues table on the page for a row click to re-scope.
   */
  | { onRollupRowSelect?: undefined; rollupSelection?: undefined };

export type GroupBySummaryPanelProps = GroupBySummaryPanelBaseProps &
  RollupSelectionProps;

interface GroupBySummaryPanelBaseProps {
  /** Base filters from the caller's own filter panel. The panel adds its own
   * drill-down field on top of these when a row/bar is expanded. */
  filters: SingleViewFilters;
  /** Which group-by buttons to show/support. */
  modes: GroupByMode[];
  existingRefs: ReadonlySet<string>;
  addRefs: (refs: ReadonlyArray<string | null | undefined>) => void;
  /**
   * Controls the active mode externally (e.g. SingleViewPage's own
   * URL-routed group-by toggle already covers switching). When set, the
   * panel hides its own ToggleButtonGroup and renders only this mode's
   * content. When omitted, the panel manages its own mode state and shows
   * its own toggle row (used by SecurityPostureReportPage).
   */
  activeMode?: GroupByMode;
}

export const GroupBySummaryPanel = ({
  filters,
  modes,
  existingRefs,
  addRefs,
  activeMode: controlledMode,
  onRollupRowSelect,
  rollupSelection,
}: GroupBySummaryPanelProps) => {
  const api = useApi(securityInsightsApiRef);
  const { hide: hideQuiesced, filterHidden } = useOptimisticQuiesceHide();
  const { teams: callerTeams } = useCallerIdentity();

  const [internalMode, setInternalMode] = useState<GroupByMode>(modes[0]);
  useEffect(() => {
    if (!modes.includes(internalMode)) setInternalMode(modes[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modes]);
  const resolvedMode = controlledMode ?? internalMode;

  // Taxonomy vocabulary — needed for Pillar/Category labels and for the
  // Pillar/Category column on every detail table this panel renders.
  const { pillarVocab, categoryVocab, pillarLabels, categoryLabels } =
    useTaxonomyLabels();

  // ── team mode state (byExperience + byTeam summary, plus the full
  // paginated Security Issues table with CSV export) ────────────────────────
  const [byExperience, setByExperience] = useState<IssuesByGroup[]>([]);
  const [byTeam, setByTeam] = useState<IssuesByGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [issuesTruncated, setIssuesTruncated] = useState(false);
  const [issueTableKey, setIssueTableKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const issuesVersionRef = useRef(0);
  const issuesHideAdjustmentRef = useRef(createHideAdjustment());

  // ── asset mode state (paginated Assets table + expandable per-asset
  // issue detail, ported from SingleViewPage's own former asset view) ──────
  const [totalAssets, setTotalAssets] = useState(0);
  const [assetsTruncated, setAssetsTruncated] = useState(false);
  const [expandedAsset, setExpandedAsset] = useState<string | null>(null);
  const [assetDetails, setAssetDetails] = useState<SecurityIssue[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [assetTableKey, setAssetTableKey] = useState(0);
  const assetDetailsSectionRef = useRef<HTMLDivElement>(null);
  const [exportingAssets, setExportingAssets] = useState(false);
  const [exportingAssetDetails, setExportingAssetDetails] = useState(false);
  const assetsVersionRef = useRef(0);

  // ── per-mode rollup state ──────────────────────────────────────────────
  const pillarState = useRollupState();
  const categoryState = useRollupState();
  const issueTypeState = useRollupState();
  const cveState = useRollupState();
  // One selection for the whole System / Deployed Instance table: a row is a
  // System or one of its instances, never both at once, and its key (see
  // `systemInstanceRowKey`) says which. The rows themselves are nested, so
  // they live beside this state rather than in its flat `rows`.
  const systemInstanceState = useRollupState();
  const [systemInstanceRows, setSystemInstanceRows] = useState<
    IssuesBySystemInstance[]
  >([]);
  const [systemInstanceFailed, setSystemInstanceFailed] = useState(false);

  const modeConfigs: Record<RollupMode, RollupModeConfig> = useMemo(
    () => ({
      pillar: {
        nameKey: 'pillar',
        noun: 'Pillar',
        getRollup: f => api.getIssuesByPillar(f),
        drillField: 'selectedPillar',
        labelFor: code => pillarLabels.get(code) ?? code,
        exportFilename: 'issues_by_pillar.csv',
      },
      category: {
        nameKey: 'category',
        noun: 'Category',
        getRollup: f => api.getIssuesByCategory(f),
        drillField: 'selectedCategory',
        labelFor: code => categoryLabels.get(code) ?? code,
        exportFilename: 'issues_by_category.csv',
      },
      issueType: {
        nameKey: 'issue_type',
        noun: 'Tool',
        getRollup: f => api.getIssuesByIssueType(f),
        drillField: 'selectedIssueType',
        labelFor: formatToolName,
        exportFilename: 'issues_by_issue_type.csv',
      },
      cve: {
        nameKey: 'cve_or_rule',
        noun: 'CVE / Rule',
        getRollup: f => api.getIssuesByCve(f),
        drillField: 'selectedCveExact',
        exportFilename: 'issues_by_cve.csv',
      },
    }),
    [api, pillarLabels, categoryLabels],
  );

  const statesByMode: Record<RollupMode | 'systemInstance', RollupState> = {
    pillar: pillarState,
    category: categoryState,
    issueType: issueTypeState,
    cve: cveState,
    systemInstance: systemInstanceState,
  };

  const pillarFetchDetails = useFetchRollupDetails(
    api,
    filters,
    modeConfigs.pillar,
    pillarState,
    addRefs,
    filterHidden,
  );
  const categoryFetchDetails = useFetchRollupDetails(
    api,
    filters,
    modeConfigs.category,
    categoryState,
    addRefs,
    filterHidden,
  );
  const issueTypeFetchDetails = useFetchRollupDetails(
    api,
    filters,
    modeConfigs.issueType,
    issueTypeState,
    addRefs,
    filterHidden,
  );
  const cveFetchDetails = useFetchRollupDetails(
    api,
    filters,
    modeConfigs.cve,
    cveState,
    addRefs,
    filterHidden,
  );
  const systemInstanceFetchDetails = useFetchRollupDetails(
    api,
    filters,
    SYSTEM_INSTANCE_CONFIG,
    systemInstanceState,
    addRefs,
    filterHidden,
  );

  const fetchDetailsByMode: Record<
    RollupMode | 'systemInstance',
    ReturnType<typeof useFetchRollupDetails>
  > = {
    pillar: pillarFetchDetails,
    category: categoryFetchDetails,
    issueType: issueTypeFetchDetails,
    cve: cveFetchDetails,
    systemInstance: systemInstanceFetchDetails,
  };

  // Reset all per-mode state when the base filters change — same reasoning
  // as SingleViewPage's handleFiltersChange: invalidate in-flight fetches
  // via version bump before resetting, and collapse any open drill-down so
  // it doesn't keep pointing at a value the new filters may not even return.
  useEffect(() => {
    ++issuesVersionRef.current;
    setTotalCount(0);
    setIssuesTruncated(false);
    setIssueTableKey(k => k + 1);
    issuesHideAdjustmentRef.current = createHideAdjustment();
    ++assetsVersionRef.current;
    setTotalAssets(0);
    setAssetsTruncated(false);
    setAssetTableKey(k => k + 1);
    setExpandedAsset(null);
    setAssetDetails([]);
    for (const state of Object.values(statesByMode)) {
      ++state.versionRef.current;
      state.setExpanded(null);
      state.setTotal(0);
      state.setTruncated(false);
      state.setTableKey(k => k + 1);
      state.hideAdjustmentRef.current = createHideAdjustment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Debounced summary/rollup fetch — same 300ms + version-guard pattern as
  // SingleViewPage's summaryVersionRef effect.
  const summaryVersionRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [summaryLoading, setSummaryLoading] = useState(true);

  useEffect(() => {
    // Asset mode is a paginated server-side table driven by its own
    // `fetchAssets` data-loader below, not this in-memory rollup fetch.
    if (resolvedMode === 'asset') return undefined;
    clearTimeout(debounceRef.current);
    const version = ++summaryVersionRef.current;
    setSummaryLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const search = filters.selectedCve?.trim() || undefined;
        if (resolvedMode === 'team') {
          const [expData, teamData] = await Promise.all([
            filters.selectedExperience === 'All'
              ? api.getIssuesByExperience(withSearch(filters, search))
              : Promise.resolve([]),
            api.getIssuesByTeam(withSearch(filters, search)),
          ]);
          if (version !== summaryVersionRef.current) return;
          setByExperience(expData);
          setByTeam(teamData);
          addRefs(teamData.map(r => toGroupRef(String(r.team ?? ''))));
        } else if (resolvedMode === 'pillarCategory') {
          // Merged group-by (SEC single-view UX request): the nested table
          // needs both rollups — pillar rows for the top-level totals,
          // category rows nested underneath via each category's pillar_code.
          const [pillarRows, categoryRows] = await Promise.all([
            modeConfigs.pillar.getRollup(withSearch(filters, search)),
            modeConfigs.category.getRollup(withSearch(filters, search)),
          ]);
          if (version !== summaryVersionRef.current) return;
          pillarState.setRows(pillarRows);
          categoryState.setRows(categoryRows);
        } else if (resolvedMode === 'systemInstance') {
          // One request: the backend nests each System's instances itself.
          const rows = await api.getIssuesBySystemInstance(
            withSearch(filters, search),
          );
          if (version !== summaryVersionRef.current) return;
          setSystemInstanceRows(withoutLoneUnattributedInstance(rows));
          setSystemInstanceFailed(false);
        } else {
          const cfg = modeConfigs[resolvedMode as RollupMode];
          const state = statesByMode[resolvedMode as RollupMode];
          const data = await cfg.getRollup(withSearch(filters, search));
          if (version !== summaryVersionRef.current) return;
          state.setRows(data);
        }
      } catch {
        if (
          resolvedMode === 'systemInstance' &&
          version === summaryVersionRef.current
        ) {
          // Not "leave it on screen" like the flat modes: this table's rows
          // would still be the previous filters' results, presented as the
          // current ones. Drop them and say the load failed.
          setSystemInstanceRows([]);
          setSystemInstanceFailed(true);
        }
        // The other modes leave their existing summary data on screen.
      } finally {
        if (version === summaryVersionRef.current) setSummaryLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, api, addRefs, resolvedMode]);

  const fetchTeamIssues = useCallback(
    async (query: {
      page: number;
      pageSize: number;
      orderBy?: { field?: string };
      orderDirection?: string;
    }) => {
      const version = ++issuesVersionRef.current;
      const search = filters.selectedCve?.trim() || undefined;
      const searchActive = Boolean(search);
      const result = await api.getSingleView({
        ...withSearch(filters, search),
        page: query.page,
        pageSize: query.pageSize,
        orderBy: searchActive ? undefined : query.orderBy?.field,
        orderDirection: searchActive
          ? undefined
          : (query.orderDirection as 'asc' | 'desc') || undefined,
      });
      if (version !== issuesVersionRef.current) {
        return { data: [], page: query.page, totalCount: 0 };
      }
      const { data, totalCount: adjustedTotalCount } = applyOptimisticHide(
        result,
        filters.includeQuiesced,
        filterHidden,
        issuesHideAdjustmentRef,
      );
      setTotalCount(adjustedTotalCount);
      setIssuesTruncated(Boolean(result.truncated));
      const refs: Array<string | null> = [];
      for (const row of result.data) {
        refs.push(toGroupRef(row.team ?? ''));
        refs.push(...assetCandidateRefs(row.asset ?? '', row.issue_type));
      }
      addRefs(refs);
      return { data, page: query.page, totalCount: adjustedTotalCount };
    },
    [api, addRefs, filterHidden, filters],
  );

  const handleExportTeamIssues = async () => {
    if (totalCount === 0) return;
    setExporting(true);
    try {
      await api.exportSingleViewCsv(
        withSearch(filters, filters.selectedCve?.trim() || undefined),
      );
    } finally {
      setExporting(false);
    }
  };

  // ── asset mode: paginated Assets table + expandable per-asset detail ────
  const fetchAssets = useCallback(
    async (query: {
      page: number;
      pageSize: number;
      orderBy?: { field?: string | number };
      orderDirection?: string;
    }) => {
      const version = ++assetsVersionRef.current;
      const orderField = query.orderBy?.field;
      const sanitisedOrderField =
        typeof orderField === 'string' ? orderField : undefined;
      const search = filters.selectedCve?.trim() || undefined;
      const searchActive = Boolean(search);
      const result = await api.getTopAssets({
        ...withSearch(filters, search),
        page: query.page,
        pageSize: query.pageSize,
        orderBy: searchActive ? undefined : sanitisedOrderField,
        orderDirection: searchActive
          ? undefined
          : (query.orderDirection as 'asc' | 'desc') || undefined,
      });
      if (version !== assetsVersionRef.current) {
        return { data: [], page: query.page, totalCount: 0 };
      }
      setTotalAssets(result.totalCount);
      setAssetsTruncated(Boolean(result.truncated));
      const refs: Array<string | null> = [];
      for (const row of result.data) {
        refs.push(toGroupRef(row.team ?? ''));
        refs.push(...assetCandidateRefs(row.asset ?? '', row.issue_type));
      }
      addRefs(refs);
      return {
        data: result.data,
        page: query.page,
        totalCount: result.totalCount,
      };
    },
    [api, addRefs, filters],
  );

  useEffect(() => {
    if (!expandedAsset) return undefined;
    const [asset, team] = expandedAsset.split('|');
    let cancelled = false;
    setDetailsLoading(true);
    api
      .getAssetDetails({
        ...withoutCve(filters),
        selectedAsset: asset,
        selectedAssetTeam: team,
      })
      .then(details => {
        if (!cancelled) setAssetDetails(details);
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [expandedAsset, filters, api]);

  const handleExpandAsset = (asset: string, team: string) => {
    const key = `${asset}|${team}`;
    if (expandedAsset === key) {
      setExpandedAsset(null);
      setAssetDetails([]);
    } else {
      setAssetDetails([]);
      setExpandedAsset(key);
      setTimeout(
        () =>
          assetDetailsSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          }),
        0,
      );
    }
  };

  const handleExportAssets = async () => {
    if (totalAssets === 0) return;
    setExportingAssets(true);
    try {
      await api.exportAssetsCsv(
        withSearch(filters, filters.selectedCve?.trim() || undefined),
      );
    } finally {
      setExportingAssets(false);
    }
  };

  const handleExportAssetDetails = async () => {
    if (!expandedAsset || assetDetails.length === 0) return;
    const [asset, team] = expandedAsset.split('|');
    setExportingAssetDetails(true);
    try {
      await api.exportAssetDetailsCsv({
        ...withoutCve(filters),
        selectedAsset: asset,
        selectedAssetTeam: team,
      });
    } finally {
      setExportingAssetDetails(false);
    }
  };

  const handleExpand = (state: RollupState, value: string) => {
    if (state.expanded === value) {
      state.setExpanded(null);
      state.setTotal(0);
      state.setTruncated(false);
      return;
    }
    state.setTotal(0);
    state.setTruncated(false);
    state.setExpanded(value);
    state.setTableKey(k => k + 1);
    setTimeout(
      () =>
        state.detailsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        }),
      100,
    );
  };

  /** True when the caller owns the selection (and the resulting issues
   *  table); see `rollupSelection`'s doc comment. */
  const selectionIsControlled = Boolean(onRollupRowSelect);

  /** The selection to highlight and to toggle against. When the caller owns
   *  it, always read theirs — never this panel's local copy, which can't see
   *  scope changes made through any other surface. */
  const effectiveExpanded = (state: RollupState) =>
    selectionIsControlled ? (rollupSelection ?? null) : state.expanded;

  /** Rollup-mode row/bar click. Delegates to `onRollupRowSelect` when the
   *  caller wants to own the resulting issues table itself (see its doc
   *  comment); otherwise falls back to this panel's own expand-and-fetch
   *  behavior via `handleExpand`. */
  const handleRollupExpand = (
    cfg: RollupModeConfig,
    state: RollupState,
    value: string,
  ) => {
    if (!onRollupRowSelect) {
      handleExpand(state, value);
      return;
    }
    // Toggle against the caller's current scope, not `state.expanded`.
    onRollupRowSelect(
      cfg.drillField,
      effectiveExpanded(state) === value ? null : value,
    );
  };

  const handleExportRollupDetails = async (
    cfg: DrillConfig,
    state: RollupState,
  ) => {
    if (!state.expanded || state.total === 0) return;
    state.setIsExporting(true);
    try {
      await api.exportSingleViewCsv({
        ...withSearch(filters, filters.selectedCve?.trim() || undefined),
        ...drillFilters(cfg, state.expanded),
      } as any);
    } finally {
      state.setIsExporting(false);
    }
  };

  const [quiesceTarget, setQuiesceTarget] = useState<SecurityIssue | null>(
    null,
  );
  const handleQuiesceSuccess = useCallback(() => {
    if (quiesceTarget) {
      hideQuiesced(quiesceTarget);
      setAssetDetails(prev => filterHidden(prev));
    }
    setIssueTableKey(k => k + 1);
    setAssetTableKey(k => k + 1);
    for (const state of Object.values(statesByMode)) {
      state.setTableKey(k => k + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiesceTarget, hideQuiesced, filterHidden]);

  const actionsColumn = useMemo(
    () => buildActionsColumn(row => setQuiesceTarget(row), callerTeams),
    [callerTeams],
  );

  const issueColumns = useMemo(() => {
    const base = buildFindingColumns(existingRefs, pillarLabels, categoryLabels);
    const withDup = filters.deduplicate ? [...base, dupColumn] : base;
    return [...withDup, actionsColumn];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    existingRefs,
    filters.deduplicate,
    actionsColumn,
    pillarLabels,
    categoryLabels,
  ]);

  // The per-asset drilldown below is already scoped to one fixed
  // asset+team pair — every row would repeat the identical Team/Asset
  // value, so drop those two columns from the shared unified set instead
  // of showing redundant, unhelpful columns.
  const assetDetailIssueColumns = useMemo(
    () => issueColumns.filter(col => col.field !== 'team' && col.field !== 'asset'),
    [issueColumns],
  );

  const severityKeys = (filters.selectedSeverities || ['CRITICAL', 'HIGH']).map(
    s => s.toLowerCase(),
  );
  const searchTerm = filters.selectedCve?.trim() || undefined;

  // ── asset mode columns ───────────────────────────────────────────────────
  const assetSeverityColumns: TableColumn<TopAsset>[] = (
    filters.selectedSeverities || []
  ).map((sev: string) => ({
    title: sev,
    field: sev.toLowerCase(),
    type: 'numeric' as const,
    ...centered,
  }));

  const assetColumns: TableColumn<TopAsset>[] = [
    {
      title: 'Asset',
      field: 'asset',
      ...centered,
      render: (row: TopAsset) => (
        <AssetCell
          asset={row.asset}
          issueType={row.issue_type}
          existingRefs={existingRefs}
          stopPropagation
        />
      ),
    },
    {
      title: 'Team',
      field: 'team',
      ...centered,
      render: (row: TopAsset) => (
        <TeamCell team={row.team} existingRefs={existingRefs} stopPropagation />
      ),
    },
    ...assetSeverityColumns,
    { title: 'Total', field: 'total_issues', type: 'numeric', ...centered },
  ];

  /** The "Issues for <noun>: <value> (N total)" paginated table shown below
   * a rollup's summary table once a row is expanded. Extracted so both
   * `renderRollupMode` and `renderPillarCategoryMode` (whose pillar/category
   * rows share the same underlying `pillarState`/`categoryState`) render it
   * identically instead of drifting apart. Returns `null` when nothing
   * should render — same "caller owns the drilled issues" carve-out as
   * before (see `onRollupRowSelect`'s doc comment). */
  const renderExpandedIssues = (mode: RollupMode | 'systemInstance') => {
    const cfg: DrillConfig =
      mode === 'systemInstance' ? SYSTEM_INSTANCE_CONFIG : modeConfigs[mode];
    const state = statesByMode[mode];
    const fetchDetails = fetchDetailsByMode[mode];
    const labelFor = cfg.labelFor ?? (code => code);

    if (!state.expanded || onRollupRowSelect) return null;

    return (
      <div ref={state.detailsRef} key={mode}>
        <Box
          mt={2}
          p={2}
          style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6">
              Issues for{' '}
              {'headingFor' in cfg
                ? cfg.headingFor(state.expanded)
                : `${cfg.noun.toLowerCase()}: ${labelFor(state.expanded)}`}{' '}
              ({state.total.toLocaleString()} total)
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleExportRollupDetails(cfg, state)}
              disabled={state.total === 0 || state.isExporting}
            >
              {state.isExporting ? 'Exporting...' : 'Export Issues CSV'}
            </Button>
          </Box>
          {state.truncated && (
            <Box mb={2}>
              <Alert severity="warning">
                Too many candidates to rank exhaustively for this search —
                results may be incomplete. Refine your search or add more
                filters to narrow the set.
              </Alert>
            </Box>
          )}
          <Table
            key={state.tableKey}
            options={{
              paging: true,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50, 100],
              emptyRowsWhenPaging: false,
              search: false,
              padding: 'dense',
              sorting: !searchTerm,
            }}
            columns={issueColumns}
            data={fetchDetails}
          />
        </Box>
      </div>
    );
  };

  const renderRollupMode = (mode: RollupMode) => {
    const cfg = modeConfigs[mode];
    const state = statesByMode[mode];
    const labelFor = cfg.labelFor ?? (code => code);
    const selected = effectiveExpanded(state);

    return (
      <>
        {summaryLoading && <SummaryPlaceholder />}
        {!summaryLoading && state.rows.length > 0 && (
          <Box mb={3}>
            <ContentHeader
              title={`Security issues by ${cfg.noun} — click a row to view issues`}
            />
            <SummaryTable
              data={state.rows}
              nameKey={cfg.nameKey}
              exportFilename={cfg.exportFilename}
              onRowClick={row =>
                handleRollupExpand(cfg, state, String(row[cfg.nameKey]))
              }
              selectedRowValue={selected}
              renderNameCell={value => labelFor(String(value))}
            />
            <SeverityBarChart
              data={state.rows}
              nameKey={cfg.nameKey}
              severityKeys={severityKeys}
              onBarClick={value => handleRollupExpand(cfg, state, value)}
              tickFormatter={labelFor}
            />
          </Box>
        )}

        {!summaryLoading && state.rows.length === 0 && !selected && (
          <Box
            mt={2}
            p={4}
            style={{
              border: '1px dashed #c0c0c0',
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="textSecondary">
              No issues match the current filters.
            </Typography>
          </Box>
        )}

        {renderExpandedIssues(mode)}
      </>
    );
  };

  /** Pillar totals as top-level rows, that pillar's categories nested
   * directly beneath (no expand/collapse — there are only a handful of
   * pillars and categories, so showing everything at once is simpler than
   * an accordion). Merges the `pillar`/`category` rollups client-side via
   * each category's `pillar_code` — the same idiom
   * `SingleViewFilterPanel`'s taxonomy `Select` and
   * `taxonomyControlStatus.ts`'s `groupCategoriesByPillar` already use. */
  const renderPillarCategoryMode = () => {
    const pillarRows = pillarState.rows;
    const categoryRows = categoryState.rows;

    const pillarRowByCode = new Map(
      pillarRows.map(row => [String(row.pillar), row]),
    );
    const categoryRowByCode = new Map(
      categoryRows.map(row => [String(row.category), row]),
    );

    // Same order the "Pillar / Category" filter dropdown uses
    // (`SingleViewFilterPanel`): Unclassified first, then pillars/categories
    // in the vocabulary's own `sort_order` — not the rollups' own
    // total-descending order, which would disagree with the filter.
    const vocabOrderedPillarCodes = [
      UNCLASSIFIED_TAXONOMY,
      ...pillarVocab.map(p => p.code),
    ].filter(code => pillarRowByCode.has(code));
    // Any pillar the rollup returned that the vocabulary doesn't know about
    // (vocab still loading, `getPillars()` failed, or a code missing from
    // the vocab) falls back to the rollup's own order instead of being
    // dropped — otherwise a vocab-fetch failure would empty the whole table
    // even though rollup data exists.
    const missingPillarCodes = pillarRows
      .map(row => String(row.pillar))
      .filter(code => !vocabOrderedPillarCodes.includes(code));
    const orderedPillarCodes = [
      ...vocabOrderedPillarCodes,
      ...missingPillarCodes,
    ];

    // Severity columns follow `severityKeys` (derived from
    // `filters.selectedSeverities`, same as the charts below) rather than
    // `Object.keys(pillarRows[0])` — the raw rollup key order can drift from
    // the filter's order and would disagree with the charts.
    const severityCols = pillarRows.length
      ? severityKeys.filter(k => k in pillarRows[0])
      : [];

    const pillarSelected = effectiveExpanded(pillarState);
    const categorySelected = effectiveExpanded(categoryState);

    const handlePillarClick = (code: string) => {
      if (!onRollupRowSelect) categoryState.setExpanded(null);
      handleRollupExpand(modeConfigs.pillar, pillarState, code);
    };
    const handleCategoryClick = (code: string) => {
      if (!onRollupRowSelect) pillarState.setExpanded(null);
      handleRollupExpand(modeConfigs.category, categoryState, code);
    };

    const totals: Record<string, number> = {};
    for (const row of pillarRows) {
      for (const col of [...severityCols, 'total_by_pillar']) {
        const value = row[col];
        if (typeof value === 'number') {
          totals[col] = (totals[col] || 0) + value;
        }
      }
    }

    return (
      <>
        {summaryLoading && <SummaryPlaceholder />}
        {!summaryLoading && pillarRows.length > 0 && (
          <Box mb={3}>
            <ContentHeader title="Security issues by Pillar / Category — click a row to view issues" />
            <TableContainer component={Paper} style={{ maxHeight: 600 }}>
              <MuiTable size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Pillar / Category
                    </TableCell>
                    {severityCols.map(col => (
                      <TableCell
                        key={col}
                        align="right"
                        style={{ fontWeight: 'bold' }}
                      >
                        {col.charAt(0).toUpperCase() + col.slice(1)}
                      </TableCell>
                    ))}
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderedPillarCodes.map(pCode => {
                    const pRow = pillarRowByCode.get(pCode)!;
                    const nestedAll = categoryVocab
                      .filter(c => c.pillar_code === pCode)
                      .map(c => categoryRowByCode.get(c.code))
                      .filter((row): row is IssuesByGroup => Boolean(row));
                    // A pillar whose only category repeats its own code
                    // (e.g. Attack Paths) would just duplicate the pillar
                    // row — same carve-out as
                    // `groupCategoriesByPillar`/`SingleViewFilterPanel`.
                    const nested =
                      nestedAll.length === 1 &&
                      String(nestedAll[0].category) === pCode
                        ? []
                        : nestedAll;
                    const pillarRowSelected = pillarSelected === pCode;
                    return (
                      <Fragment key={pCode}>
                        <TableRow
                          hover
                          onClick={() => handlePillarClick(pCode)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handlePillarClick(pCode);
                            }
                          }}
                          tabIndex={0}
                          selected={pillarRowSelected}
                          aria-selected={pillarRowSelected || undefined}
                          style={{ cursor: 'pointer' }}
                        >
                          <TableCell style={{ fontWeight: 600 }}>
                            {pillarLabels.get(pCode) ?? pCode}
                          </TableCell>
                          {severityCols.map(col => (
                            <TableCell
                              key={col}
                              align="right"
                              style={{ fontWeight: 600 }}
                            >
                              {typeof pRow[col] === 'number'
                                ? (pRow[col] as number).toLocaleString()
                                : ''}
                            </TableCell>
                          ))}
                          <TableCell align="right" style={{ fontWeight: 600 }}>
                            {typeof pRow.total_by_pillar === 'number'
                              ? pRow.total_by_pillar.toLocaleString()
                              : ''}
                          </TableCell>
                        </TableRow>
                        {nested.map(cRow => {
                          const cCode = String(cRow.category);
                          const categoryRowSelected =
                            categorySelected === cCode;
                          return (
                            <TableRow
                              key={cCode}
                              hover
                              onClick={() => handleCategoryClick(cCode)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleCategoryClick(cCode);
                                }
                              }}
                              tabIndex={0}
                              selected={categoryRowSelected}
                              aria-selected={categoryRowSelected || undefined}
                              style={{ cursor: 'pointer' }}
                            >
                              <TableCell style={{ paddingLeft: 32 }}>
                                {categoryLabels.get(cCode) ?? cCode}
                              </TableCell>
                              {severityCols.map(col => (
                                <TableCell key={col} align="right">
                                  {typeof cRow[col] === 'number'
                                    ? (cRow[col] as number).toLocaleString()
                                    : ''}
                                </TableCell>
                              ))}
                              <TableCell align="right">
                                {typeof cRow.total_by_category === 'number'
                                  ? cRow.total_by_category.toLocaleString()
                                  : ''}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>TOTALS</TableCell>
                    {severityCols.map(col => (
                      <TableCell
                        key={col}
                        align="right"
                        style={{ fontWeight: 'bold' }}
                      >
                        {(totals[col] || 0).toLocaleString()}
                      </TableCell>
                    ))}
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>
                      {(totals.total_by_pillar || 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </MuiTable>
            </TableContainer>
            <SeverityBarChart
              data={orderedPillarCodes.map(code => pillarRowByCode.get(code)!)}
              nameKey="pillar"
              severityKeys={severityKeys}
              onBarClick={value => handlePillarClick(value)}
              tickFormatter={code => pillarLabels.get(code) ?? code}
            />
          </Box>
        )}

        {!summaryLoading &&
          pillarRows.length === 0 &&
          !pillarSelected &&
          !categorySelected && (
            <Box
              mt={2}
              p={4}
              style={{
                border: '1px dashed #c0c0c0',
                borderRadius: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="textSecondary">
                No issues match the current filters.
              </Typography>
            </Box>
          )}

        {renderExpandedIssues('pillar')}
        {renderExpandedIssues('category')}
      </>
    );
  };

  /** Systems as top-level rows with the Deployed Instances seen under each
   * nested directly beneath (always shown, like Pillar / Category — there are
   * few enough that an accordion would only hide the point). Unlike
   * `renderPillarCategoryMode` the nesting isn't assembled here: the backend
   * returns each System with its `instances`, because no client-side
   * vocabulary says which System an instance belongs to.
   *
   * A System with no deployed instances of its own is just its System row (see
   * `withoutLoneUnattributedInstance`).
   *
   * Clicking a System scopes to it; clicking one of its instances scopes to
   * that instance *within* that System — both filters, since an instance
   * name alone is ambiguous (every System has an `Unattributed` one). */
  const renderSystemInstanceMode = () => {
    const state = systemInstanceState;
    const selected = effectiveExpanded(state);

    // Severity columns follow `severityKeys` (the filter's order, like the
    // charts) rather than the raw rollup's key order.
    const severityCols = systemInstanceRows.length
      ? severityKeys.filter(k => k in systemInstanceRows[0])
      : [];

    const handleClick = (system: string, instance?: string) => {
      const key = systemInstanceRowKey(system, instance);
      if (!onRollupRowSelect) {
        handleExpand(state, key);
        return;
      }
      // Toggle against the caller's current scope, not `state.expanded`.
      const deselect = selected === key;
      if (instance === undefined) {
        onRollupRowSelect('selectedSystem', deselect ? null : system);
      } else {
        onRollupRowSelect(
          'selectedDeployedInstance',
          deselect ? null : instance,
          { selectedSystem: system },
        );
      }
    };

    // Same shape as `SummaryTable`'s CSV, flattened to one row per table row:
    // a System's own totals first, then one row per instance with the System
    // repeated, so the file reads like the table does. `level` says which kind
    // each row is, because a System's total already includes its instances'
    // and one Total column would otherwise be summed twice.
    const exportCsv = () => {
      const flat: IssuesByGroup[] = [];
      const totals: IssuesByGroup = { system: 'TOTALS' };
      for (const row of systemInstanceRows) {
        const { instances, ...systemTotals } = row;
        flat.push({
          ...systemTotals,
          deployment_instance: '',
          level: 'System',
        } as IssuesByGroup);
        for (const instance of instances) {
          const { total_by_deployed_instance: total, ...counts } = instance;
          flat.push({
            ...counts,
            system: row.system,
            level: 'Deployed Instance',
            total_by_system: total,
          } as IssuesByGroup);
        }
        for (const col of [...severityCols, 'total_by_system']) {
          const value = row[col];
          if (typeof value === 'number') {
            totals[col] = ((totals[col] as number) || 0) + value;
          }
        }
      }
      exportTableCsv(
        flat,
        totals,
        [
          'system',
          'deployment_instance',
          'level',
          ...severityCols,
          'total_by_system',
        ],
        SYSTEM_INSTANCE_CONFIG.exportFilename,
      );
    };

    const totals: Record<string, number> = {};
    for (const row of systemInstanceRows) {
      for (const col of [...severityCols, 'total_by_system']) {
        const value = row[col];
        if (typeof value === 'number') {
          totals[col] = (totals[col] || 0) + value;
        }
      }
    }

    const cell = (row: IssuesByGroup | IssuesBySystemInstance, col: string) => {
      const value = row[col];
      return typeof value === 'number' ? value.toLocaleString() : '';
    };

    const clickableRowProps = (
      rowKey: string,
      system: string,
      instance?: string,
    ) => {
      const isSelected = selected === rowKey;
      return {
        hover: true,
        tabIndex: 0,
        selected: isSelected,
        'aria-selected': isSelected || undefined,
        style: { cursor: 'pointer' },
        onClick: () => handleClick(system, instance),
        onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(system, instance);
          }
        },
      };
    };

    return (
      <>
        {summaryLoading && <SummaryPlaceholder />}
        {!summaryLoading && systemInstanceRows.length > 0 && (
          <Box mb={3}>
            <ContentHeader title="Security issues by System / Deployed Instance — click a row to view issues" />
            <Box mb={1} display="flex" justifyContent="flex-end">
              <Button variant="outlined" size="small" onClick={exportCsv}>
                Export CSV
              </Button>
            </Box>
            <TableContainer component={Paper} style={{ maxHeight: 600 }}>
              <MuiTable size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      System / Deployed Instance
                    </TableCell>
                    {severityCols.map(col => (
                      <TableCell
                        key={col}
                        align="right"
                        style={{ fontWeight: 'bold' }}
                      >
                        {col.charAt(0).toUpperCase() + col.slice(1)}
                      </TableCell>
                    ))}
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {systemInstanceRows.map(row => {
                    const systemKey = systemInstanceRowKey(row.system);
                    return (
                      <Fragment key={systemKey}>
                        <TableRow {...clickableRowProps(systemKey, row.system)}>
                          <TableCell style={{ fontWeight: 600 }}>
                            {row.system}
                          </TableCell>
                          {severityCols.map(col => (
                            <TableCell
                              key={col}
                              align="right"
                              style={{ fontWeight: 600 }}
                            >
                              {cell(row, col)}
                            </TableCell>
                          ))}
                          <TableCell align="right" style={{ fontWeight: 600 }}>
                            {cell(row, 'total_by_system')}
                          </TableCell>
                        </TableRow>
                        {row.instances.map(instance => {
                          const name = String(instance.deployment_instance);
                          const instanceKey = systemInstanceRowKey(
                            row.system,
                            name,
                          );
                          return (
                            <TableRow
                              key={instanceKey}
                              {...clickableRowProps(
                                instanceKey,
                                row.system,
                                name,
                              )}
                            >
                              <TableCell style={{ paddingLeft: 32 }}>
                                {name}
                              </TableCell>
                              {severityCols.map(col => (
                                <TableCell key={col} align="right">
                                  {cell(instance, col)}
                                </TableCell>
                              ))}
                              <TableCell align="right">
                                {cell(instance, 'total_by_deployed_instance')}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>TOTALS</TableCell>
                    {severityCols.map(col => (
                      <TableCell
                        key={col}
                        align="right"
                        style={{ fontWeight: 'bold' }}
                      >
                        {(totals[col] || 0).toLocaleString()}
                      </TableCell>
                    ))}
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>
                      {(totals.total_by_system || 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </MuiTable>
            </TableContainer>
            <SeverityBarChart
              data={systemInstanceRows.map(
                ({ instances: _instances, ...systemTotals }) =>
                  systemTotals as IssuesByGroup,
              )}
              nameKey="system"
              severityKeys={severityKeys}
              onBarClick={value => handleClick(value)}
            />
          </Box>
        )}

        {!summaryLoading && systemInstanceFailed && (
          <Box mt={2}>
            <Alert severity="error">
              Couldn't load the System / Deployed Instance breakdown. Change a
              filter to try again.
            </Alert>
          </Box>
        )}

        {!summaryLoading &&
          !systemInstanceFailed &&
          systemInstanceRows.length === 0 &&
          // A selection only stands in for this message when the panel renders
          // that selection's issues itself; a caller who owns the selection
          // renders them elsewhere, which would leave this panel blank.
          (!selected || selectionIsControlled) && (
            <Box
              mt={2}
              p={4}
              style={{
                border: '1px dashed #c0c0c0',
                borderRadius: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="textSecondary">
                No issues match the current filters.
              </Typography>
            </Box>
          )}

        {renderExpandedIssues('systemInstance')}
      </>
    );
  };

  return (
    <>
      {!controlledMode && (
        <Box mb={2} display="flex" justifyContent="flex-end">
          <GroupByControl
            value={internalMode}
            onChange={setInternalMode}
            options={modes.map(mode => ({
              value: mode,
              label: MODE_LABELS[mode],
            }))}
          />
        </Box>
      )}

      {resolvedMode === 'team' && (
        <>
          {summaryLoading ? (
            <>
              {(!filters || filters.selectedExperience === 'All') && (
                <SummaryPlaceholder />
              )}
              <SummaryPlaceholder />
            </>
          ) : (
            <>
              {byExperience.length > 0 && (
                <Box mb={3}>
                  <ContentHeader title="Security issues by Experience" />
                  <SummaryTable
                    data={byExperience}
                    nameKey="experience_slug"
                    exportFilename="issues_by_experience.csv"
                  />
                  <SeverityBarChart
                    data={byExperience}
                    nameKey="experience_slug"
                    severityKeys={severityKeys}
                  />
                </Box>
              )}

              {byTeam.length > 0 &&
                (!filters.selectedTeam || filters.selectedTeam === 'All') && (
                  <Box mb={3}>
                    <ContentHeader
                      title={
                        filters.selectedExperience &&
                        filters.selectedExperience !== 'All'
                          ? `Security issues by Team (for ${filters.selectedExperience})`
                          : 'Security issues by Team'
                      }
                    />
                    <SummaryTable
                      data={byTeam}
                      nameKey="team"
                      exportFilename="issues_by_team.csv"
                      renderNameCell={(slug, stopPropagation) => (
                        <TeamCell
                          team={typeof slug === 'string' ? slug : String(slug)}
                          existingRefs={existingRefs}
                          stopPropagation={stopPropagation}
                        />
                      )}
                    />
                    <SeverityBarChart
                      data={byTeam}
                      nameKey="team"
                      severityKeys={severityKeys}
                    />
                  </Box>
                )}
            </>
          )}

          <ContentHeader
            title={`Security Issues (${totalCount.toLocaleString()} total)`}
          >
            <Button
              variant="outlined"
              onClick={handleExportTeamIssues}
              disabled={totalCount === 0 || exporting}
            >
              {exporting
                ? 'Exporting...'
                : `Export CSV (${totalCount.toLocaleString()} rows)`}
            </Button>
          </ContentHeader>
          {issuesTruncated && (
            <Box mb={2}>
              <Alert severity="warning">
                Too many candidates to rank exhaustively for this search —
                results may be incomplete. Refine your search or add more
                filters to narrow the set.
              </Alert>
            </Box>
          )}
          <Table
            key={issueTableKey}
            options={{
              paging: true,
              pageSize: 20,
              pageSizeOptions: [20, 50, 100],
              emptyRowsWhenPaging: false,
              search: false,
              padding: 'dense',
              toolbar: false,
              sorting: !searchTerm,
            }}
            columns={issueColumns}
            data={fetchTeamIssues}
          />
        </>
      )}

      {resolvedMode === 'asset' && (
        <>
          <Box mb={1} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleExportAssets}
              disabled={totalAssets === 0 || exportingAssets}
            >
              {exportingAssets
                ? 'Exporting...'
                : `Export Assets CSV (${totalAssets.toLocaleString()} rows)`}
            </Button>
          </Box>
          {assetsTruncated && (
            <Box mb={2}>
              <Alert severity="warning">
                Too many candidates to rank exhaustively for this search —
                results may be incomplete. Refine your search or add more
                filters to narrow the set.
              </Alert>
            </Box>
          )}
          <Table
            key={assetTableKey}
            title={`Assets (${totalAssets.toLocaleString()} total) — click a row to view issues`}
            options={{
              paging: true,
              pageSize: 20,
              pageSizeOptions: [20, 50, 100],
              emptyRowsWhenPaging: false,
              search: false,
              padding: 'dense',
              tableLayout: 'auto',
              sorting: !searchTerm,
              rowStyle: (row: TopAsset) => ({
                cursor: 'pointer',
                backgroundColor:
                  expandedAsset === `${row.asset}|${row.team}`
                    ? 'rgba(0, 0, 0, 0.04)'
                    : undefined,
              }),
            }}
            columns={assetColumns}
            data={fetchAssets}
            onRowClick={(_e, row) =>
              row && handleExpandAsset(row.asset, row.team)
            }
          />

          {expandedAsset && (
            <div ref={assetDetailsSectionRef}>
              <Box
                mt={2}
                p={2}
                style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}
              >
                {detailsLoading && <LinearProgress />}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="h6">
                    Issues for: {expandedAsset.split('|')[0]} (
                    {expandedAsset.split('|')[1] || 'Unknown'})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportAssetDetails}
                    disabled={
                      assetDetails.length === 0 || exportingAssetDetails
                    }
                  >
                    {exportingAssetDetails
                      ? 'Exporting...'
                      : 'Export Issues CSV'}
                  </Button>
                </Box>
                <Table
                  options={{
                    paging: true,
                    pageSize: 10,
                    emptyRowsWhenPaging: false,
                    search: true,
                    padding: 'dense',
                  }}
                  columns={assetDetailIssueColumns}
                  data={assetDetails}
                  isLoading={detailsLoading}
                />
              </Box>
            </div>
          )}
        </>
      )}

      {resolvedMode === 'pillarCategory' && renderPillarCategoryMode()}

      {resolvedMode === 'systemInstance' && renderSystemInstanceMode()}

      {resolvedMode !== 'team' &&
        resolvedMode !== 'asset' &&
        resolvedMode !== 'pillarCategory' &&
        resolvedMode !== 'systemInstance' &&
        renderRollupMode(resolvedMode)}

      {quiesceTarget && (
        <QuiesceDialog
          open
          tool={quiesceTarget.tool}
          srcId={quiesceTarget.src_id}
          asset={quiesceTarget.asset}
          team={quiesceTarget.team}
          cveOrRule={quiesceTarget.cve_or_rule}
          onClose={() => setQuiesceTarget(null)}
          onSuccess={handleQuiesceSuccess}
        />
      )}
    </>
  );
};
